"use client";

import React, { useState } from "react";
import { cn } from "@/lib/utils";

export interface TooltipProps {
  children: React.ReactNode;
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, position = "top", className }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 transform -translate-x-1/2 border-t-gray-800",
    bottom: "bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-800",
    left: "left-full top-1/2 transform -translate-y-1/2 border-l-gray-800",
    right: "right-full top-1/2 transform -translate-y-1/2 border-r-gray-800",
  };

  if (content === "") {
    return <>{children}</>;
  }

  return (
    <div
      className="relative inline-block cursor-pointer"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
      onKeyDown={e => {
        if (e.key === "Escape") {
          setIsVisible(false);
        }
      }}
      role="button"
      tabIndex={0}
      aria-describedby={
        isVisible ? `tooltip-${Math.random().toString(36).substr(2, 9)}` : undefined
      }
    >
      {children}
      {isVisible && (
        <div
          id={`tooltip-${Math.random().toString(36).substr(2, 9)}`}
          className={cn(
            "absolute z-50 px-4 py-4 text-xs text-gray-700 bg-gray-50 rounded shadow-lg whitespace-nowrap",
            positionClasses[position],
            className
          )}
          role="tooltip"
        >
          {content}
          <div
            className={cn("absolute w-0 h-0 border-4 border-transparent", arrowClasses[position])}
          />
        </div>
      )}
    </div>
  );
};

export { Tooltip };
